package com.evoapps.evoshift.domain.billing

import kotlin.time.Duration.Companion.days
import kotlin.time.Instant

/**
 * THE plan definition of EVoShift (SH-38, SH-52) - the one place to change when product decides.
 *
 * EVoShift is paid only. The plan belongs to the owner (the billing entity, `owners/{uid}`):
 * [TRIAL_DAYS] free from their first workplace, without a card; then they buy a seat tier in the
 * store and RevenueCat grants its entitlements to their Firebase uid: the tier's
 * `evoshift_premium_<seats>` and the legacy [PREMIUM_ENTITLEMENT_ID] ([resolvePlan]).
 * Employees never see a paywall.
 *
 * A plan is sized in seats: each person with an active engagement in any of the owner's
 * workplaces takes one, the owner none. The tiers are not listed here: they are the packages of
 * the RevenueCat offering, and a product's seats are the number after "seats" in its identifier
 * ([seatsOf]), so tiers change in RevenueCat without a release. functions/planAccess.js reads the
 * same convention for the plan every member sees (`workplaces/{id}/plan/access`); keep them in step.
 */
object Plans {
    /**
     * The RevenueCat entitlement every product grants (compared case-insensitively). Old app
     * versions check only it, so it stays attached to every product. On its own, without a
     * [tier entitlement][tierEntitlementId], its seats come from its product id, and a grant
     * whose product names none (a promotional grant from the dashboard) is [LEGACY_GRANT_SEATS].
     */
    const val PREMIUM_ENTITLEMENT_ID = "evoshift_premium"

    /** The per-tier entitlements, `evoshift_premium_<seats>`: what a promotional grant picks a tier by. */
    const val TIER_ENTITLEMENT_PREFIX = "evoshift_premium_"

    /** The seats of [PREMIUM_ENTITLEMENT_ID] granted without a seat number: the biggest tier. */
    const val LEGACY_GRANT_SEATS = 100

    /**
     * The RevenueCat offering shown on the paywall; null shows the project's current offering
     * (RevenueCat dashboard > Offerings > "current"), which lets marketing switch offerings
     * without a release.
     */
    val OFFERING_ID: String? = null

    /** The free trial from the owner's first workplace, without a card (planAccess.js TRIAL_DAYS). */
    const val TRIAL_DAYS = 45

    /** Seats of a product whose identifier names none, e.g. RevenueCat's Test Store `monthly`. */
    const val FALLBACK_SEATS = 10

    /** Workplaces on every plan: what firestore.rules can check per person (unrolled for 6). */
    const val MAX_WORKPLACES = 6

    /**
     * Whether the plan is enforced where this build cannot sell one (no RevenueCat key for the
     * platform, e.g. local and emulator builds): false keeps such builds fully usable instead of
     * locking people behind a paywall they cannot open.
     */
    const val ENFORCE_LIMITS_WITHOUT_BILLING = false

    /**
     * SH-220: where a team bigger than every tier writes ("Larger team? Write to us"): the
     * support address of docs/support-email.md. No tier above the offering's is ever listed.
     */
    const val SALES_CONTACT_EMAIL = "contact@evoshift.app"

    /** SH-220: days before the renewal when the over-plan reminder names the renewal date. */
    const val OVER_PLAN_REMINDER_DAYS = 7

    /** Paid periods shown on the paywall, the preselected one first. */
    val OFFERED_PERIODS: List<BillingPeriod> = listOf(BillingPeriod.YEARLY, BillingPeriod.MONTHLY)

    private val SEATS = Regex("""seats[_-]?(\d+)""", RegexOption.IGNORE_CASE)

    /** The seats [productId] (else [packageId]) names, e.g. `evoshift_seats_25:monthly` -> 25; null if none. */
    fun seatsOf(productId: String?, packageId: String? = null): Int? =
        listOf(productId, packageId).firstNotNullOfOrNull { id ->
            id?.let { SEATS.find(it) }?.groupValues?.get(1)?.toIntOrNull()?.takeIf { it > 0 }
        }

    /** [seatsOf], or [FALLBACK_SEATS] when the ids name none. */
    fun seatsFor(productId: String?, packageId: String? = null): Int = seatsOf(productId, packageId) ?: FALLBACK_SEATS

    fun trialEndsAt(trialStartedAt: Instant): Instant = trialStartedAt + TRIAL_DAYS.days

    fun limits(tier: PlanTier, seats: Int): PlanLimits = when (tier) {
        PlanTier.TRIAL -> PlanLimits(MAX_WORKPLACES, PlanLimits.UNLIMITED)
        PlanTier.PREMIUM -> PlanLimits(MAX_WORKPLACES, seats)
        // Read-only: nothing new until the owner chooses a plan.
        PlanTier.NONE -> PlanLimits(0, 0)
    }

    /** `evoshift_premium_25` for 25 seats. */
    fun tierEntitlementId(seats: Int): String = "$TIER_ENTITLEMENT_PREFIX$seats"

    /** The seats a tier entitlement names (`evoshift_premium_25` -> 25); null for any other id. */
    fun tierSeatsOf(entitlementId: String): Int? {
        val id = entitlementId.trim()
        if (!id.startsWith(TIER_ENTITLEMENT_PREFIX, ignoreCase = true)) return null
        val number = id.substring(TIER_ENTITLEMENT_PREFIX.length)
        if (number.isEmpty() || !number.all { it in '0'..'9' }) return null
        return number.toIntOrNull()?.takeIf { it > 0 }
    }

    fun isLegacyEntitlement(entitlementId: String): Boolean =
        entitlementId.trim().equals(PREMIUM_ENTITLEMENT_ID, ignoreCase = true)

    /** Whether [entitlementId] gives a plan: a tier entitlement or [PREMIUM_ENTITLEMENT_ID]. */
    fun isPremiumEntitlement(entitlementId: String): Boolean =
        isLegacyEntitlement(entitlementId) || tierSeatsOf(entitlementId) != null

    /**
     * The seats one plan entitlement gives: a tier entitlement its number; [PREMIUM_ENTITLEMENT_ID]
     * the seats of its product, or [LEGACY_GRANT_SEATS] when that names none. Null: no plan entitlement.
     */
    fun seatsOfEntitlement(entitlement: Entitlement): Int? = when {
        tierSeatsOf(entitlement.id) != null -> tierSeatsOf(entitlement.id)
        isLegacyEntitlement(entitlement.id) -> seatsOf(entitlement.productId) ?: LEGACY_GRANT_SEATS
        else -> null
    }

    /**
     * THE seat rule (functions/planAccess.js activePlan does the same; keep them in step): the
     * highest active tier entitlement; else [PREMIUM_ENTITLEMENT_ID] active on its own, with the
     * seats of its product - or of the biggest active subscription in [subscriptionEnds] that
     * names seats, as two products can run while a plan changes - or [LEGACY_GRANT_SEATS] for a
     * grant without a seat number. Null without an active plan entitlement.
     */
    fun resolvePlan(
        entitlements: List<Entitlement>,
        now: Instant,
        subscriptionEnds: Map<String, Instant?> = emptyMap()
    ): PlanEntitlement? {
        val active = entitlements.filter { isPremiumEntitlement(it.id) && it.isActiveAt(now) }
        active.mapNotNull { e -> tierSeatsOf(e.id)?.let { PlanEntitlement(e, it) } }
            .maxWithOrNull(compareBy<PlanEntitlement> { it.seats }.thenBy { it.entitlement.expiresAt ?: Instant.DISTANT_FUTURE })
            ?.let { return it }
        val legacy = active.filter { isLegacyEntitlement(it.id) }.maxByOrNull { it.expiresAt ?: Instant.DISTANT_FUTURE } ?: return null
        val running = subscriptionEnds
            .filter { (_, end) -> end == null || end > now }
            .keys.mapNotNull { seatsOf(it) }
        return PlanEntitlement(legacy, (running + seatsOfEntitlement(legacy)!!).max())
    }

    /**
     * The plan the owner pays for in a store ([BillingStore.hasOwnManagement]), not a promotional
     * grant: the one a tier change replaces. [resolvePlan] over those entitlements.
     */
    fun resolveStorePlan(
        entitlements: List<Entitlement>,
        now: Instant,
        subscriptionEnds: Map<String, Instant?> = emptyMap()
    ): PlanEntitlement? = resolvePlan(entitlements.filter { it.store.hasOwnManagement }, now, subscriptionEnds)

    /**
     * SH-220: the smallest of the seat [tiers] (from the offering, [seatsOf]) that holds a team of
     * [team] people (the owner not counted); null when none does: a team that big writes to us.
     */
    fun recommendedTier(tiers: Collection<Int>, team: Int): Int? = tiers.distinct().sorted().firstOrNull { it >= team }

    /**
     * SH-220: the tier to grow into from a plan of [currentSeats] once the team has [team]
     * people: the smallest bigger tier that holds them; null while the plan fits, or when no
     * tier is big enough.
     */
    fun nextTier(tiers: Collection<Int>, currentSeats: Int, team: Int): Int? =
        if (team <= currentSeats) null else tiers.distinct().sorted().firstOrNull { it > currentSeats && it >= team }

    /**
     * SH-220: the one plan the paywall proposes, from the store's [offers]: for a team of [team]
     * the [recommendedTier], or with a running plan of [currentSeats] the [nextTier] (null while
     * it fits). Of that tier, the period of the running plan ([currentProductId]) so a change
     * stays on it, else an offer with a discounted introductory price (the launch offer), else
     * monthly, else in the order of [OFFERED_PERIODS]. Never the product already paid for.
     */
    fun recommendedOffer(
        offers: List<PlanOffer>,
        team: Int,
        currentSeats: Int? = null,
        currentProductId: String? = null
    ): PlanOffer? {
        val tiers = offers.map { it.seats }
        val seats = (if (currentSeats != null) nextTier(tiers, currentSeats, team) else recommendedTier(tiers, team)) ?: return null
        val candidates = offers.filter { it.seats == seats && it.productId != currentProductId }
        val currentPeriod = offers.firstOrNull { it.productId == currentProductId }?.period
        return candidates.firstOrNull { currentPeriod != null && it.period == currentPeriod }
            ?: candidates.firstOrNull { it.introductoryOffer?.let { intro -> !intro.isFreeTrial && intro.formattedPrice != null } == true }
            ?: candidates.firstOrNull { it.period == BillingPeriod.MONTHLY }
            ?: candidates.minByOrNull { offer -> OFFERED_PERIODS.indexOf(offer.period).let { if (it < 0) Int.MAX_VALUE else it } }
    }

    /** The tier a store package unlocks: every package grants a plan entitlement. */
    @Suppress("UNUSED_PARAMETER")
    fun tierForPackage(packageId: String, productId: String): PlanTier = PlanTier.PREMIUM
}

/** A plan entitlement and the seats it gives ([Plans.resolvePlan]). */
data class PlanEntitlement(val entitlement: Entitlement, val seats: Int)

/** Where the owner's plan stands; there is no free plan. */
enum class PlanTier {
    /** The free months from the first workplace: everything, for the whole team. */
    TRIAL,
    /** A bought plan of some seats. */
    PREMIUM,
    /** The trial is over and nothing is bought: read-only until the owner chooses a plan. */
    NONE
}

/** What the owner's plan allows. [UNLIMITED] means no limit. */
data class PlanLimits(
    val maxWorkplaces: Int,
    val maxSeats: Int
) {
    fun of(resource: PlanResource): Int = when (resource) {
        PlanResource.WORKPLACES -> maxWorkplaces
        PlanResource.SEATS -> maxSeats
    }

    companion object {
        const val UNLIMITED = Int.MAX_VALUE
    }
}

/** Things a plan limits. */
enum class PlanResource {
    WORKPLACES,
    /** People with an active engagement in any of the owner's workplaces, the owner not counted. */
    SEATS
}
